import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Calculator, TrendingUp, Info, Wallet } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const STORAGE_KEY = 'salary_breakdown_data';

export const SalaryBreakdownCalculator = () => {
  const [grossSalary, setGrossSalary] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).grossSalary : 100000;
  });
  
  const [basicPayPercent, setBasicPayPercent] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).basicPayPercent : 40;
  });
  
  const [hra, setHra] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).hra : 20000;
  });
  
  const [specialAllowance, setSpecialAllowance] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).specialAllowance : 15000;
  });
  
  const [otherAllowances, setOtherAllowances] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).otherAllowances : 5000;
  });
  
  const [pfDeduction, setPfDeduction] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).pfDeduction : 1800;
  });
  
  const [taxDeduction, setTaxDeduction] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).taxDeduction : 5000;
  });
  
  const [otherDeductions, setOtherDeductions] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).otherDeductions : 2000;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      grossSalary,
      basicPayPercent,
      hra,
      specialAllowance,
      otherAllowances,
      pfDeduction,
      taxDeduction,
      otherDeductions
    }));
  }, [grossSalary, basicPayPercent, hra, specialAllowance, otherAllowances, pfDeduction, taxDeduction, otherDeductions]);

  const calculate = () => {
    const gross = parseFloat(grossSalary) || 0;
    const basicPercent = parseFloat(basicPayPercent) || 0;
    const hraAmount = parseFloat(hra) || 0;
    const specialAllow = parseFloat(specialAllowance) || 0;
    const otherAllow = parseFloat(otherAllowances) || 0;
    const pf = parseFloat(pfDeduction) || 0;
    const tax = parseFloat(taxDeduction) || 0;
    const otherDed = parseFloat(otherDeductions) || 0;

    const basicPay = (gross * basicPercent) / 100;
    const totalAllowances = hraAmount + specialAllow + otherAllow;
    const totalDeductions = pf + tax + otherDed;
    const netSalary = gross - totalDeductions;
    const ctc = gross * 12;
    const annualTakeHome = netSalary * 12;

    return {
      basicPay,
      totalAllowances,
      totalDeductions,
      netSalary,
      ctc,
      annualTakeHome,
      deductionPercent: gross > 0 ? (totalDeductions / gross) * 100 : 0
    };
  };

  const generateSalaryBreakdownData = () => {
    const gross = parseFloat(grossSalary) || 0;
    const basicPercent = parseFloat(basicPayPercent) || 0;
    const hraAmount = parseFloat(hra) || 0;
    const specialAllow = parseFloat(specialAllowance) || 0;
    const otherAllow = parseFloat(otherAllowances) || 0;

    const basicPay = (gross * basicPercent) / 100;

    return [
      { name: 'Basic Pay', value: basicPay },
      { name: 'HRA', value: hraAmount },
      { name: 'Special Allowance', value: specialAllow },
      { name: 'Other Allowances', value: otherAllow }
    ];
  };

  const generateDeductionData = () => {
    const pf = parseFloat(pfDeduction) || 0;
    const tax = parseFloat(taxDeduction) || 0;
    const otherDed = parseFloat(otherDeductions) || 0;

    return [
      { name: 'PF', value: pf },
      { name: 'Tax', value: tax },
      { name: 'Other', value: otherDed }
    ];
  };

  const generateComparisonData = () => {
    const results = calculate();
    
    return [
      { category: 'Gross Salary', amount: parseFloat(grossSalary) || 0 },
      { category: 'Total Allowances', amount: results.totalAllowances },
      { category: 'Total Deductions', amount: results.totalDeductions },
      { category: 'Net Salary', amount: results.netSalary }
    ];
  };

  const results = calculate();
  const salaryBreakdownData = generateSalaryBreakdownData();
  const deductionData = generateDeductionData();
  const comparisonData = generateComparisonData();

  const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center">
              <Wallet className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-display text-3xl font-semibold text-foreground">
              Salary Breakdown Calculator
            </h1>
          </div>
          <p className="text-muted-foreground">Understand your salary structure and optimize your income</p>
        </div>

        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is Salary Breakdown?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Salary breakdown helps you understand how your gross salary is structured into various components like basic pay, 
                  allowances, and deductions. This clarity helps in tax planning and understanding your actual take-home salary.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Input Parameters</CardTitle>
                  <CardDescription>Enter your salary details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="grossSalary" className="text-sm font-medium">
                      Gross Salary (Monthly) ($)
                    </Label>
                    <Input
                      id="grossSalary"
                      type="number"
                      value={grossSalary}
                      onChange={(e) => setGrossSalary(e.target.value)}
                      placeholder="100000"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="basicPayPercent" className="text-sm font-medium">
                      Basic Pay (%)
                    </Label>
                    <Input
                      id="basicPayPercent"
                      type="number"
                      value={basicPayPercent}
                      onChange={(e) => setBasicPayPercent(e.target.value)}
                      placeholder="40"
                      className="text-base"
                    />
                  </div>

                  <Separator />
                  <p className="text-sm font-semibold text-foreground">Allowances</p>

                  <div className="space-y-2">
                    <Label htmlFor="hra" className="text-sm font-medium">
                      HRA ($)
                    </Label>
                    <Input
                      id="hra"
                      type="number"
                      value={hra}
                      onChange={(e) => setHra(e.target.value)}
                      placeholder="20000"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="specialAllowance" className="text-sm font-medium">
                      Special Allowance ($)
                    </Label>
                    <Input
                      id="specialAllowance"
                      type="number"
                      value={specialAllowance}
                      onChange={(e) => setSpecialAllowance(e.target.value)}
                      placeholder="15000"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="otherAllowances" className="text-sm font-medium">
                      Other Allowances ($)
                    </Label>
                    <Input
                      id="otherAllowances"
                      type="number"
                      value={otherAllowances}
                      onChange={(e) => setOtherAllowances(e.target.value)}
                      placeholder="5000"
                      className="text-base"
                    />
                  </div>

                  <Separator />
                  <p className="text-sm font-semibold text-foreground">Deductions</p>

                  <div className="space-y-2">
                    <Label htmlFor="pfDeduction" className="text-sm font-medium">
                      PF Deduction ($)
                    </Label>
                    <Input
                      id="pfDeduction"
                      type="number"
                      value={pfDeduction}
                      onChange={(e) => setPfDeduction(e.target.value)}
                      placeholder="1800"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="taxDeduction" className="text-sm font-medium">
                      Tax Deduction ($)
                    </Label>
                    <Input
                      id="taxDeduction"
                      type="number"
                      value={taxDeduction}
                      onChange={(e) => setTaxDeduction(e.target.value)}
                      placeholder="5000"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="otherDeductions" className="text-sm font-medium">
                      Other Deductions ($)
                    </Label>
                    <Input
                      id="otherDeductions"
                      type="number"
                      value={otherDeductions}
                      onChange={(e) => setOtherDeductions(e.target.value)}
                      placeholder="2000"
                      className="text-base"
                    />
                  </div>

                  <Button 
                    className="w-full gradient-primary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setGrossSalary(grossSalary)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Salary Breakdown Results</CardTitle>
                    <CardDescription>Your salary structure</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <Wallet className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Basic Pay</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {formatCurrency(results.basicPay)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">{basicPayPercent}% of gross</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Allowances</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {formatCurrency(results.totalAllowances)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-destructive/10 to-card border border-destructive/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-destructive flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Deductions</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-destructive">
                          {formatCurrency(results.totalDeductions)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">{results.deductionPercent.toFixed(1)}% of gross</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <Wallet className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Net Salary (Monthly)</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent">
                          {formatCurrency(results.netSalary)}
                        </p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="p-6 rounded-xl bg-gradient-chart">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">CTC (Annual)</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {formatCurrency(results.ctc)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Annual Take-Home</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {formatCurrency(results.annualTakeHome)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Salary Structure Breakdown</CardTitle>
                <CardDescription className="text-base">
                  Visual representation of your salary components
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={salaryBreakdownData}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        label={(entry) => `${entry.name}: ${formatCurrency(entry.value)}`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {salaryBreakdownData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Allowances vs Deductions</CardTitle>
                  <CardDescription>
                    Compare your income components
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={comparisonData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="category" 
                          stroke="hsl(var(--muted-foreground))"
                          angle={-15}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="amount" fill="hsl(var(--primary))" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Deduction Breakdown</CardTitle>
                  <CardDescription>
                    Where your deductions go
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={deductionData}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          label={(entry) => `${entry.name}: ${formatCurrency(entry.value)}`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {deductionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Your Salary Structure</CardTitle>
                <CardDescription className="text-base">
                  Learn how to optimize your salary and plan better
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    CTC vs In-Hand Salary
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Your CTC (Cost to Company) is <strong className="text-primary">{formatCurrency(results.ctc)}</strong> annually, 
                    but your actual take-home is <strong className="text-primary">{formatCurrency(results.annualTakeHome)}</strong>. 
                    The difference of <strong>{formatCurrency(results.ctc - results.annualTakeHome)}</strong> goes towards various deductions.
                  </p>
                  <div className="p-4 rounded-lg bg-muted/50 text-sm">
                    <p className="mb-2">The gap exists because:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Provident Fund contributions (Employee + Employer)</li>
                      <li>Income tax deductions (TDS)</li>
                      <li>Professional tax and other statutory deductions</li>
                    </ul>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Tax-Efficient Salary Structuring
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Maximize HRA</h4>
                      <p className="text-muted-foreground">
                        HRA is partially tax-exempt. Keep it high if you're paying rent to reduce taxable income.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Optimize Basic Pay</h4>
                      <p className="text-muted-foreground">
                        While basic pay affects PF contributions, keeping it around 40-50% is generally optimal for balanced benefits.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">✅ Use Special Allowances</h4>
                      <p className="text-muted-foreground">
                        Special allowances for travel, mobile, etc. can be structured to be tax-efficient with proper bills.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Hidden Deduction Impact
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Your total deductions are <strong className="text-accent">{formatCurrency(results.totalDeductions)}</strong> per month, 
                    which is <strong>{results.deductionPercent.toFixed(1)}%</strong> of your gross salary.
                  </p>
                  <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Monthly Deductions:</span>
                        <span className="font-semibold text-foreground">{formatCurrency(results.totalDeductions)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Annual Deductions:</span>
                        <span className="font-semibold text-foreground">{formatCurrency(results.totalDeductions * 12)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Effective Take-Home %:</span>
                        <span className="font-semibold text-foreground">{((results.netSalary / parseFloat(grossSalary)) * 100).toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Important Considerations
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Employer PF Contribution:</strong> Remember that your employer also contributes 
                        to PF (usually 12% of basic), which adds to your retirement corpus.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Tax Planning:</strong> Use tax-saving instruments (80C, 80D, etc.) 
                        to reduce your taxable income and increase take-home.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Professional Tax:</strong> This varies by state and is typically 
                        capped at $2,500 annually.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Optimization Tips
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">💡 Negotiate Smart</h4>
                      <p className="text-muted-foreground">
                        Focus on CTC components that are tax-efficient like flexible benefits, stock options, and performance bonuses.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">💡 Review Annually</h4>
                      <p className="text-muted-foreground">
                        Restructure your salary every year based on tax regime changes and personal circumstances.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">💡 Invest Your PF</h4>
                      <p className="text-muted-foreground">
                        Your PF earns ~8% annual interest and is a great retirement corpus. Don't withdraw it prematurely.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SalaryBreakdownCalculator;
